import {
  AutoAwesomeRounded,
  CheckCircleOutlineRounded,
  DescriptionOutlined,
  KeyboardArrowRightRounded,
  LocalOfferOutlined,
  WarningAmberRounded,
} from '@mui/icons-material'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material'
import React from 'react'
import { UseFormHandleSubmit } from 'react-hook-form'
import { colors } from '../../../theme'
import { AdoptionFormParams } from './AnimalAdoptionTab'

interface Props {
  isOpen: boolean
  onClose: () => void
  handleSubmit: UseFormHandleSubmit<AdoptionFormParams>
  onSubmit: (data: AdoptionFormParams, confirmation?: boolean) => void
  alreadyExistingAttempt: boolean
  oldAdopterName?: string
}

export const CreateAdoptionAttemptConfirmationModal: React.FC<Props> = ({
  isOpen,
  onClose,
  handleSubmit,
  onSubmit,
  alreadyExistingAttempt,
  oldAdopterName,
}) => {
  const newProcedureBenefits = [
    {
      icon: <LocalOfferOutlined sx={{ fontSize: 18, color: colors.cello }} />,
      title: "Renseignez les frais d'adoption",
      subtitle: 'Montant, acompte et suivi du paiement au même endroit.',
    },
    {
      icon: <DescriptionOutlined sx={{ fontSize: 18, color: colors.cello }} />,
      title: 'Préparez vos documents',
      subtitle: 'Utilisez vos modèles préconfigurés pour générer certificat et contrat.',
    },
    {
      icon: <CheckCircleOutlineRounded sx={{ fontSize: 18, color: colors.cello }} />,
      title: 'Centralisez le suivi',
      subtitle: "Gardez une vue claire sur l'avancement de l'adoption et les actions restantes.",
    },
  ]

  return (
    <Dialog
      maxWidth="sm"
      open={isOpen}
      onClose={onClose}
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: { xs: 3, sm: 4 },
          border: '1px solid #E4E8F2',
          boxShadow: '0 24px 64px rgba(32, 56, 100, 0.16)',
          overflow: 'hidden',
        },
      }}
      sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflowY: 'hidden' }}
    >
      <DialogTitle
        sx={{
          px: { xs: 2.25, sm: 3 },
          pt: { xs: 2.25, sm: 2.75 },
          pb: 1.25,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '14px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: alreadyExistingAttempt
                ? 'linear-gradient(135deg, #FFF5D9 0%, #FFE7AD 100%)'
                : 'linear-gradient(135deg, #EEF4FF 0%, #DCEAFF 100%)',
              color: colors.cello,
              flexShrink: 0,
            }}
          >
            {alreadyExistingAttempt ? <WarningAmberRounded /> : <AutoAwesomeRounded />}
          </Box>
          <Box>
            <Typography sx={{ fontSize: { xs: 22, sm: 26 }, fontWeight: 800, color: colors.cello, lineHeight: 1.1 }}>
              {alreadyExistingAttempt ? 'Confirmer le changement d’adoptant' : 'Configurer l’adoption'}
            </Typography>
            <Typography sx={{ mt: 0.5, fontSize: 13.5, lineHeight: 1.45, color: '#66718A' }}>
              {alreadyExistingAttempt
                ? 'Vous allez interrompre la procédure actuelle pour poursuivre avec un autre adoptant.'
                : 'Lancez la procédure Petso et préparez la suite dans un flux simple et guidé.'}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <Box
        sx={{
          maxHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'hidden',
          boxSizing: 'content-box',
        }}
      >
        <DialogContent
          sx={{
            px: { xs: 2.25, sm: 3 },
            pb: { xs: 2.25, sm: 2.75 },
            overflowY: 'auto',
            maxHeight: 'calc(100vh - 230px)',
          }}
        >
          {alreadyExistingAttempt ? (
            <Box
              sx={{
                borderRadius: 3,
                border: '1px solid rgba(252, 180, 2, 0.32)',
                backgroundColor: '#FFF9E8',
                px: { xs: 1.5, sm: 2 },
                py: { xs: 1.5, sm: 1.75 },
              }}
            >
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: colors.cello, lineHeight: 1.4 }}>
                Vous êtes sur le point de suspendre une procédure d’adoption.
              </Typography>
              <Typography sx={{ mt: 1, fontSize: 14, color: colors.cello, lineHeight: 1.55 }}>
                Si vous continuez, la procédure actuellement liée à{' '}
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {oldAdopterName}
                </Box>{' '}
                sera suspendue.
              </Typography>
              <Typography sx={{ mt: 1, fontSize: 13.5, color: '#66718A', lineHeight: 1.5 }}>
                Le nouvel adoptant pourra ensuite reprendre le suivi dans une nouvelle procédure.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.25}>
              <Typography sx={{ fontSize: 14.5, color: colors.cello, lineHeight: 1.55 }}>
                Via Petso, gérez votre procédure d&apos;adoption en quelques clics :
              </Typography>
              {newProcedureBenefits.map((benefit) => (
                <Box
                  key={benefit.title}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.25,
                    p: 1.4,
                    borderRadius: 3,
                    border: '1px solid rgba(122, 129, 151, 0.16)',
                    backgroundColor: '#FBFCFE',
                  }}
                >
                  <Box
                    sx={{
                      mt: 0.2,
                      width: 30,
                      height: 30,
                      borderRadius: 2.5,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#EEF4FF',
                      flexShrink: 0,
                    }}
                  >
                    {benefit.icon}
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: colors.cello, lineHeight: 1.35 }}>
                      {benefit.title}
                    </Typography>
                    <Typography sx={{ mt: 0.35, fontSize: 13.5, color: '#66718A', lineHeight: 1.45 }}>
                      {benefit.subtitle}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2.25, sm: 3 },
            pb: { xs: 2.25, sm: 2.75 },
            pt: 0,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            alignItems: 'stretch',
          }}
        >
          <Button
            variant="outlined"
            type="button"
            onClick={onClose}
            sx={{
              minHeight: 46,
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 700,
              borderColor: 'rgba(38, 62, 120, 0.18)',
              color: colors.cello,
              px: 2,
            }}
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            type="button"
            onClick={handleSubmit((data) => onSubmit(data, true))}
            endIcon={<KeyboardArrowRightRounded />}
            sx={{
              minHeight: 46,
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 800,
              px: 2.2,
              boxShadow: '0 10px 22px rgba(252, 180, 2, 0.28)',
            }}
          >
            C&apos;est parti !
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}
